/**
 * 受取予定 — web orders waiting at this shop's counter (T07-40, T07-42, R19/R21). The list is
 * pos-service's `GET /pos/pickups` (order-service `pickup_ready` orders of the register's
 * location — T05-30 `filter[pickupLocation]`); it refetches by itself when
 *
 * - the hub's `pos:register:<id>` says a **pickup** order moved to / from `pickup_ready` /
 *   `picked_up` (`order.status.changed` hint `from` / `to`) or was cancelled / refunded — the hub
 *   sends pickup orders to every open register (T14-15: it does not know which register stands at
 *   which shop) with `hint.locationId` = the shop the order is picked up at, so an order of
 *   another shop is ignored here. No `staff:orders` any more (that one needs `orders.read` on the
 *   stream's bearer; the register topic does not);
 * - pos-service's `sale.changed` says a pickup was handed over at this register (`picked_up`).
 *
 * A new order in the list → the 受取 menu button shows the count and, if switched on in 設定, a
 * short chime + a toast 「受取のご注文が届きました」 (the counter hears it without watching the
 * screen). The layout owns the one subscription; the 受取 screen reads the same query.
 */
import { type ChangeMessage } from "@tcg/realtime";
import { useRealtime } from "@tcg/realtime/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { usePos } from "@/app/context";
import { call } from "@/lib/api";
import { POS_TOPICS } from "@/lib/sse";

export const pickupsKey = ["pos-pickups", "list", "today"] as const;
export const PICKUP_SOUND_SETTING = "pickupSound";

const PICKUP_STATES = new Set(["pickup_ready", "picked_up"]);
const ORDER_ENDINGS = new Set(["cancelled", "refunded"]);

const REGISTER_TOPIC_PREFIX = "pos:register:";

/**
 * Does this message change the 受取予定 list of the register at `locationId`?
 * (`locationId` null = not known yet → any pickup change counts.)
 */
export function isPickupChange(message: ChangeMessage, locationId: string | null = null): boolean {
  if (message.topic === POS_TOPICS.sale) {
    const payload = (message.payload ?? {}) as { action?: unknown; kind?: unknown };
    return payload.action === "picked_up" || payload.kind === "pickup";
  }
  if (!message.topic.startsWith(REGISTER_TOPIC_PREFIX) || message.entity !== "order") {
    return false;
  }
  const hint = message.hint ?? {};
  const shop = typeof hint.locationId === "string" ? hint.locationId : "";
  if (shop && locationId && shop !== locationId) {
    return false; // picked up at another shop
  }
  if (ORDER_ENDINGS.has(message.action)) {
    return true;
  }
  if (message.action !== "status_changed") {
    return false; // `paid` = a sale of this register (取引履歴)
  }
  return PICKUP_STATES.has(String(hint.to ?? "")) || PICKUP_STATES.has(String(hint.from ?? ""));
}

async function fetchPickups(api: ReturnType<typeof usePos>["api"]) {
  return call(() => api.GET("/v1/pos/pickups", { params: { query: { limit: 50 } } }));
}

export type PickupPage = Awaited<ReturnType<typeof fetchPickups>>;

/** The 受取予定 list (shared by the menu badge and the 受取 screen). */
export function usePickups(enabled: boolean) {
  const { api } = usePos();
  return useQuery({
    queryKey: pickupsKey,
    queryFn: () => fetchPickups(api),
    enabled,
    staleTime: 10_000,
  });
}

export interface PickupFeedOptions {
  enabled: boolean;
  /** `pos:register:<id>` of this tablet (null before pairing). */
  topic: string | null;
  /** The register's shop (orders of other shops are not in its list). */
  locationId: string | null;
  /** A new order arrived in the list (not on the first read). */
  onArrived?: (count: number) => void;
}

/**
 * The layout's subscription: keeps {@link pickupsKey} fresh and tells when a new order arrived.
 * Returns the number of orders waiting (for the badge).
 */
export function usePickupFeed({
  enabled,
  topic,
  locationId,
  onArrived,
}: PickupFeedOptions): number {
  const query = usePickups(enabled);
  const refetch = query.refetch;
  useRealtime(
    topic ? [topic, POS_TOPICS.sale] : [POS_TOPICS.sale],
    (message) => {
      if (isPickupChange(message, locationId)) {
        void refetch();
      }
    },
    { enabled, onResync: () => void refetch() },
  );

  const known = useRef<Set<string> | null>(null);
  const arrived = useRef(onArrived);
  useEffect(() => {
    arrived.current = onArrived;
  });
  const rows = query.data?.data;
  useEffect(() => {
    if (!rows) {
      return;
    }
    const ids = new Set(rows.map((r) => r.orderId));
    const before = known.current;
    known.current = ids;
    if (before === null) {
      return; // the first read is not "new"
    }
    const fresh = [...ids].filter((id) => !before.has(id)).length;
    if (fresh > 0) {
      arrived.current?.(fresh);
    }
  }, [rows]);
  return enabled ? (rows?.length ?? 0) : 0;
}
